import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private authService = inject(AuthService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.headers.has('Authorization')) {
      return next.handle(this.withSession(req)).pipe(
        catchError((error) => this.handleSessionError(error, req, next))
      );
    }

    return from(this.authService.getAccessToken()).pipe(
      concatMap((token) => {
        let cloned = req;
        if (token) {
          cloned = cloned.clone({
            headers: cloned.headers.set('Authorization', `Bearer ${token}`)
          });
        }
        cloned = this.withSession(cloned);
        return next.handle(cloned).pipe(
          catchError((error) => this.handleSessionError(error, cloned, next))
        );
      })
    );
  }

  private withSession(req: HttpRequest<any>): HttpRequest<any> {
    const sid = this.authService.getSessionId();
    if (sid && !req.headers.has('X-Session-Id')) {
      return req.clone({ headers: req.headers.set('X-Session-Id', sid) });
    }
    return req;
  }

  private handleSessionError(error: any, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const code = error?.error?.code;
    // This device was revoked: wipe everything (MSAL cache included) so it
    // cannot silently re-authenticate, then force an interactive login.
    if (error instanceof HttpErrorResponse && error.status === 403 && code === 'SESSION_REVOKED') {
      this.authService.handleSessionRevoked();
      this.router.navigate(['/signin'], { replaceUrl: true });
      return throwError(() => error);
    }
    // Session unknown (stale id, fresh tab): heal once by establishing and
    // retrying the request a single time, otherwise fall back to sign-in.
    if (
      error instanceof HttpErrorResponse &&
      error.status === 403 &&
      code === 'SESSION_REQUIRED' &&
      !req.url.includes('/user/v1/sessions/establish')
    ) {
      return from(this.authService.ensureSession()).pipe(
        concatMap((ok) => {
          if (!ok) {
            this.redirectToSignIn();
            return throwError(() => error);
          }
          return next.handle(this.withSession(req)).pipe(
            catchError((retryErr) => {
              this.redirectToSignIn();
              return throwError(() => retryErr);
            })
          );
        }),
        catchError(() => {
          this.redirectToSignIn();
          return throwError(() => error);
        })
      );
    }
    return this.handleAuthError(error);
  }

  private handleAuthError(error: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (error.status === 401 && !this.router.url.startsWith('/signin')) {
      this.redirectToSignIn();
    }
    return throwError(() => error);
  }

  private redirectToSignIn(): void {
    sessionStorage.clear();
    localStorage.removeItem('userDetails');
    this.router.navigate(['/signin'], { replaceUrl: true });
  }
}
